import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { updateAppBadge } from "@/lib/push";
import {
  LayoutDashboard, Flag, Building2, Users, Inbox, CreditCard, Star, BarChart3, Settings, LogOut, ExternalLink, ShieldCheck, DollarSign, Send,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/races", label: "Races", icon: Flag },
  { to: "/admin/organizers", label: "Organizers", icon: Building2 },
  { to: "/admin/users", label: "Users / Runners", icon: Users },
  { to: "/admin/submissions", label: "Submissions", icon: Inbox },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/pricing", label: "Pricing", icon: DollarSign },
  { to: "/admin/featured", label: "Featured Races", icon: Star },
  { to: "/admin/outreach", label: "Outreach", icon: Send },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    updateAppBadge();
    const id = setInterval(updateAppBadge, 60000);
    return () => clearInterval(id);
  }, [user, loc.pathname]);

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#0a1420]"><div className="w-10 h-10 border-4 border-slate-700 border-t-[#22d3ee] rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  if (user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-[#0a1420] text-slate-300 fixed inset-y-0 left-0 z-40">
        <div className="p-5 border-b border-slate-800">
          <div className="font-display font-extrabold text-xl leading-none"><span className="text-white">YourRace</span><span className="text-[#22d3ee]">Finder</span></div>
          <div className="flex items-center gap-1.5 mt-1 text-[10px] font-display font-semibold tracking-[0.2em] text-[#22d3ee]"><ShieldCheck size={12} /> ADMIN CONTROL</div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.split(" ")[0].toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-[#22d3ee] text-[#0a1420]" : "text-slate-300 hover:bg-navy-card"}`}>
                <Icon size={18} />{n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-400 hover:bg-navy-card"><ExternalLink size={16} /> View Website</a>
          <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-400 hover:bg-navy-card"><LogOut size={16} /> Log Out</button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0a1420] text-white flex items-center gap-3 px-4 h-14 overflow-x-auto no-scrollbar">
        <span className="font-display font-extrabold">Admin</span>
        {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.split(" ")[0].toLowerCase()}`} className={({ isActive }) => `text-xs whitespace-nowrap px-2 py-1 rounded ${isActive ? "text-[#22d3ee]" : "text-slate-300"}`}>{n.label}</NavLink>)}
      </div>

      <main className="flex-1 md:ml-60 pt-14 md:pt-0 min-w-0">
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex items-center justify-between mb-6">
            <div />
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="w-8 h-8 rounded-full bg-[#0a1420] text-[#22d3ee] grid place-items-center font-bold">{user.name?.[0]}</span>
              <div className="text-right leading-tight hidden sm:block"><div className="font-semibold text-slate-800">{user.name}</div><div className="text-xs text-slate-400">System Administrator</div></div>
            </div>
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
